// useMountScope — the ONE derivation of an app-mount's scope facts from the URL + scope graph.
// Extracted from SubAppPage so both the primary route mount AND the split-view secondary pane
// (same scope, different app) resolve scope identically (no duplicated, drift-prone logic).
//
// Scope comes from the URL PARAMS directly (race-free — the ContextChain scalars lag by one render
// post-navigation). Level is the node's OWN type in the hydrated scope graph on the generic
// /n/$node route, else the typed scalars. `levelPending` suppresses a false "denied" flash while
// the node route's level is still resolving (scopeNodes not yet hydrated).
import { useParams } from "@tanstack/react-router";
import type { ScopeLevel } from "@perlab/registry";
import type { ScopeNodeResp } from "../api/scopeTree";
import { useContextChain } from "./ContextChain";

export interface MountScope {
  scopeNodeId: string;
  scopePath: string;
  currentLevel: ScopeLevel;
  levelPending: boolean;
  levelError: "unavailable" | "missing" | null;
  nodeRoute: boolean;
  nodeType: ScopeNodeResp["node_type"] | undefined;
}

export function useMountScope(): MountScope {
  const { ws, client, eng, project, node } = useParams({ strict: false }) as {
    ws?: string; client?: string; eng?: string; project?: string; node?: string;
  };
  const { scopeNodes, scopeTreeStatus } = useContextChain();
  const nodeType = node ? scopeNodes.find((n) => n.id === node)?.node_type : undefined;
  const levelFromNode: ScopeLevel | undefined =
    nodeType === "workspace" || nodeType === "client" || nodeType === "engagement" || nodeType === "project"
      ? nodeType
      : undefined;
  const scopeNodeId = node || project || eng || client || ws || "";
  const currentLevel: ScopeLevel =
    levelFromNode ?? (project ? "project" : eng ? "engagement" : client ? "client" : "workspace");
  // Keep unresolved generic routes closed even after a failed/empty lookup.
  // Mount consumers can show levelError instead of an indefinite skeleton;
  // consumers checking only levelPending still cannot use a fallback level.
  const levelPending = Boolean(node) && !levelFromNode;
  const levelError = !levelPending ? null
    : scopeTreeStatus === "error" ? "unavailable"
    : scopeTreeStatus === "ready" ? "missing"
    : null;
  const scopePath = scopeNodes.find((n) => n.id === scopeNodeId)?.path ?? "";
  return {
    scopeNodeId,
    scopePath,
    currentLevel,
    levelPending,
    levelError,
    nodeRoute: Boolean(node),
    nodeType,
  };
}
